// Layer "misc" v2: spaghetti ensemble (marker + pannello iframe, port
// map-spaghetti.js) e insets isole minori (mini-mappe Pantelleria/Pelagie,
// port map-island-insets.js in chiave MapLibre).
import { state as stationsState, isFutureHidden } from './stations.js';
import { buildStationSprite, stationSpriteKey, stationHasWindVane } from './station-sprites.js';
import { forecastItemsInBounds } from './forecast.js';
import { registerInterpolInset } from './interpol.js';

const SPAG_EMBED = 'https://wslam.weathersicily.it/Copernicus/spaghetti/index.html';

let map = null;

// ---------- Spaghetti ---------------------------------------------------------
let spagOn = false;
let spagPanel = null;

function esc(s) { return String(s ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])); }

function openSpagPanel(slug, label, province) {
    closeSpagPanel();
    const src = `${SPAG_EMBED}?embedded=1&location=${encodeURIComponent(slug)}`;
    spagPanel = document.createElement('div');
    spagPanel.className = 'wsv2-panel wsv2-spag';
    spagPanel.innerHTML = `
        <header><span>🍝 Spaghi · ${esc(label)}${province ? ` <i>(${esc(province)})</i>` : ''}</span>
        <a href="${esc(src)}" target="_blank" rel="noopener">Apri ↗</a>
        <button data-close>✕</button></header>
        <iframe loading="lazy" title="Spaghi ensemble ${esc(label)}" src="${esc(src)}" referrerpolicy="no-referrer-when-downgrade"></iframe>`;
    document.body.appendChild(spagPanel);
    spagPanel.querySelector('[data-close]').addEventListener('click', closeSpagPanel);
}
function closeSpagPanel() { spagPanel?.remove(); spagPanel = null; }

export async function setSpaghettiEnabled(on) {
    spagOn = on;
    const SRC = 'ws-spag';
    if (!on) {
        closeSpagPanel();
        try { if (map.getLayer(SRC)) map.removeLayer(SRC); if (map.getSource(SRC)) map.removeSource(SRC); } catch (e) {}
        return;
    }
    const res = await fetch('/assets/data/spaghetti-locations.json', { cache: 'no-cache', credentials: 'omit' });
    const json = await res.json();
    const locations = json.locations || json;
    const feats = [];
    for (const [slug, loc] of Object.entries(locations)) {
        if (!Number.isFinite(loc.lat) || !Number.isFinite(loc.lon ?? loc.lng)) continue;
        feats.push({
            type: 'Feature',
            geometry: { type: 'Point', coordinates: [loc.lon ?? loc.lng, loc.lat] },
            properties: { slug, label: loc.label || slug, province: loc.province || '', capital: slug.indexOf('_') === -1 ? 1 : 0 },
        });
    }
    if (!map.getSource(SRC)) {
        map.addSource(SRC, { type: 'geojson', data: { type: 'FeatureCollection', features: feats } });
        map.addLayer({
            id: SRC, type: 'circle', source: SRC,
            paint: {
                'circle-radius': ['case', ['==', ['get', 'capital'], 1], 14, 11],
                'circle-color': ['case', ['==', ['get', 'capital'], 1], '#0a6473', '#0d7a8c'],
                'circle-stroke-color': ['case', ['==', ['get', 'capital'], 1], '#ffd34d', '#ffffff'],
                'circle-stroke-width': 2,
            },
        });
        map.on('click', SRC, (e) => {
            const f = e.features?.[0];
            if (f && spagOn) openSpagPanel(f.properties.slug, f.properties.label, f.properties.province);
        });
        map.on('mouseenter', SRC, () => { map.getCanvas().style.cursor = 'pointer'; });
        map.on('mouseleave', SRC, () => { map.getCanvas().style.cursor = ''; });
    } else {
        map.getSource(SRC).setData({ type: 'FeatureCollection', features: feats });
    }
}

// ---------- Insets isole (Pantelleria + Pelagie), default ON come v1 ----------
// `prov`: provincia di appartenenza dell'isola. Se è attivo il filtro di quella
// provincia si mostra SOLO il suo riquadro (AG→Pelagie, TP→Pantelleria).
// Centro sull'ISOLA intera (non solo l'abitato): il punto previsione di
// Pantelleria è nel SE dell'isola (~12.05,36.76), le stazioni live nel NW
// (~11.95,36.8) — inquadratura e zoom scelti perché entrambi restino in vista
// nel riquadro 140×96 (prima il punto previsione cadeva fuori → riquadro vuoto).
const INSETS = [
    { id: 'pantelleria', label: 'Pantelleria', center: [11.99, 36.79], zoom: 9.0, prov: 'TP' },
    { id: 'pelagie', label: 'Isole Pelagie', center: [12.6083, 35.52], zoom: 8.4, prov: 'AG' },
];
const insetMaps = new Map();
let insetsBox = null;
let insetsOn = false;
let insetsOnly = null;   // override esplicito (bot): lista di id, vince su tutto
let insetShownIds = [];  // id attualmente montati, per rimontare solo se cambiano

// Riquadro isola in modalità FUTURO: al posto dei pallini live mostra la
// PREVISIONE dell'isola (Pantelleria/Lampedusa) allo step corrente della timeline.
function renderInsetForecast(im) {
    const src = im.getSource('ins-st');
    if (!src) return;
    const b = im.getBounds();
    // Piccolo margine: il punto previsione dell'isola può cadere proprio sul bordo
    // del mini-riquadro; con un filo di padding lo peschiamo comunque.
    const P = 0.06;
    const items = forecastItemsInBounds(b.getWest() - P, b.getEast() + P, b.getSouth() - P, b.getNorth() + P);
    const scale = window.WSPalettes && window.WSPalettes.scaleForParam ? window.WSPalettes.scaleForParam('temperatura') : null;
    const feats = [];
    for (const it of items) {
        let color = '#ef7d2e';
        try { const s = scale && scale(it.temp); if (s) color = s.hex ? s.hex() : String(s); } catch (e) {}
        const txt = String(Math.round(it.temp));
        const key = 'insfc|' + txt + '|' + color;
        if (!im.hasImage(key)) {
            const sp = buildStationSprite({ lon: it.lon, lat: it.lat, text: txt, color, station: {} },
                'temperatura', 'low', { windEnabled: false, altitudeLabel: '', badgesVisible: false });
            const c = document.createElement('canvas');
            const W = 2 * Math.max(sp.ax, sp.w - sp.ax), H = 2 * Math.max(sp.ay, sp.h - sp.ay);
            c.width = Math.ceil(W * sp.dpr); c.height = Math.ceil(H * sp.dpr);
            const ctx = c.getContext('2d');
            ctx.setTransform(sp.dpr, 0, 0, sp.dpr, 0, 0);
            ctx.drawImage(sp.canvas, W / 2 - sp.ax, H / 2 - sp.ay, sp.w, sp.h);
            im.addImage(key, ctx.getImageData(0, 0, c.width, c.height), { pixelRatio: sp.dpr });
        }
        feats.push({ type: 'Feature', geometry: { type: 'Point', coordinates: [it.lon, it.lat] }, properties: { sk: key } });
    }
    src.setData({ type: 'FeatureCollection', features: feats });
}

function renderInsetStations(im, def) {
    // Timeline nel FUTURO → niente pallini live: nel riquadro va la PREVISIONE
    // dell'isola (come la mappa principale, che nel futuro passa alle previsioni).
    if (isFutureHidden()) { renderInsetForecast(im); return; }
    // "Mostra stazioni" OFF → anche gli inset restano puliti (parità v1
    // map-island-insets: currentRecords ritorna [] se stazioni nascoste).
    const b = im.getBounds();
    // "Mostra direzione": anche gli inset mostrano le banderuole (parità mappa
    // principale) e saltano le stazioni senza dato vento.
    const windOn = !!stationsState.options.wind;
    // Legge i record SENZA filtro geografico (recordsAll): così il riquadro isola
    // mostra le sue stazioni anche quando la mappa principale è ristretta a una
    // provincia (Agrigento → Pelagie, Trapani → Pantelleria). Fallback a records.
    const pool = stationsState.recordsAll && stationsState.recordsAll.length ? stationsState.recordsAll : stationsState.records;
    let recs = !stationsState.options.visible ? []
        : pool.filter(r => r.lon >= b.getWest() && r.lon <= b.getEast() && r.lat >= b.getSouth() && r.lat <= b.getNorth());
    if (windOn) recs = recs.filter(r => stationHasWindVane(r.station, stationsState.parametro));
    const feats = [];
    for (const rec of recs) {
        const opts = { windEnabled: windOn, mapBearing: im.getBearing(), altitudeLabel: '', badgesVisible: false };
        const key = 'ins|' + stationSpriteKey(rec, stationsState.parametro, 'low', opts);
        if (!im.hasImage(key)) {
            const sp = buildStationSprite(rec, stationsState.parametro, 'low', opts);
            const c = document.createElement('canvas');
            const W = 2 * Math.max(sp.ax, sp.w - sp.ax), H = 2 * Math.max(sp.ay, sp.h - sp.ay);
            c.width = Math.ceil(W * sp.dpr); c.height = Math.ceil(H * sp.dpr);
            const ctx = c.getContext('2d');
            ctx.setTransform(sp.dpr, 0, 0, sp.dpr, 0, 0);
            ctx.drawImage(sp.canvas, W / 2 - sp.ax, H / 2 - sp.ay, sp.w, sp.h);
            im.addImage(key, ctx.getImageData(0, 0, c.width, c.height), { pixelRatio: sp.dpr });
        }
        feats.push({ type: 'Feature', geometry: { type: 'Point', coordinates: [rec.lon, rec.lat] }, properties: { sk: key } });
    }
    const src = im.getSource('ins-st');
    if (src) src.setData({ type: 'FeatureCollection', features: feats });
}

// Quali riquadri mostrare, in ordine: override esplicito del bot se presente;
// altrimenti, se è attiva una provincia con isola (AG/TP) SOLO quella; altrimenti
// tutti (vista Sicilia intera). Restituisce la lista di id.
function desiredInsetIds() {
    if (Array.isArray(insetsOnly) && insetsOnly.length) return insetsOnly;
    const prov = stationsState.filters && stationsState.filters.province;
    const matched = INSETS.filter(d => d.prov === prov).map(d => d.id);
    return matched.length ? matched : INSETS.map(d => d.id);
}

function teardownInsets() {
    insetsBox?.remove(); insetsBox = null;
    insetMaps.forEach(m => m.remove()); insetMaps.clear();
    insetShownIds = [];
}

function buildInset(def) {
    const cell = document.createElement('div');
    cell.className = 'wsv2-panel wsv2-inset';
    cell.innerHTML = `<i>${def.label}</i><div class="wsv2-inset-map" id="inset-${def.id}"></div>`;
    insetsBox.appendChild(cell);
    const im = new maplibregl.Map({
        container: cell.querySelector('.wsv2-inset-map'),
        style: {
            version: 8,
            sources: { esri: { type: 'raster', tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'], tileSize: 256, maxzoom: 19 } },
            layers: [{ id: 'b', type: 'raster', source: 'esri' }],
        },
        center: def.center, zoom: def.zoom, interactive: false, attributionControl: false,
    });
    im.on('load', () => {
        im.addSource('ins-st', { type: 'geojson', data: { type: 'FeatureCollection', features: [] } });
        im.addLayer({ id: 'ins-st', type: 'symbol', source: 'ins-st', layout: { 'icon-image': ['get', 'sk'], 'icon-allow-overlap': true } });
        registerInterpolInset(im);
        // Prima popolazione a prova di race (tile ESRI vs API stazioni): se i
        // record non ci sono ancora al 'load' dell'inset, riprova finché
        // arrivano, poi si ferma. Gli update live restano gestiti da
        // wsv2:stations. Senza questo lo scatto bot coglieva l'inset vuoto.
        let tries = 0, drawn = false;
        const tick = () => {
            if (drawn || !insetsOn || !im.getSource('ins-st')) return true;
            if (stationsState.recordsAll.length || stationsState.records.length) { renderInsetStations(im, def); drawn = true; return true; }
            return (++tries >= 15);
        };
        if (!tick()) { const iv = setInterval(() => { if (tick()) clearInterval(iv); }, 400); }
    });
    cell.addEventListener('click', () => map.flyTo({ center: def.center, zoom: 11 }));
    insetMaps.set(def.id, im);
}

// Allinea i riquadri montati a quelli desiderati (in base a provincia/override).
// Rimonta SOLO se l'insieme cambia: sugli update live (stessa provincia) è no-op.
function syncInsets() {
    if (!insetsOn) { teardownInsets(); return; }
    const want = desiredInsetIds();
    const same = want.length === insetShownIds.length && want.every(id => insetShownIds.includes(id));
    if (same && insetsBox) return;
    teardownInsets();
    insetsBox = document.createElement('div');
    insetsBox.className = 'wsv2-insets';
    document.body.appendChild(insetsBox);
    for (const def of INSETS.filter(d => want.includes(d.id))) buildInset(def);
    insetShownIds = want.slice();
}

// `only`: lista opzionale di id inset (es. ['pelagie']); usata dai rendering bot
// per forzare un solo riquadro. Senza `only`, l'insieme si deriva dalla provincia
// attiva (AG→Pelagie, TP→Pantelleria, altrimenti tutti).
export function setInsetsEnabled(on, only = null) {
    // WS Intelligence usa l'intera mappa come superficie di analisi: gli inset
    // non devono neppure restare montati e consumare GPU dietro un display:none.
    // Il guard e' circoscritto al prodotto, quindi Maps ordinaria resta invariata.
    insetsOn = Boolean(on) && !document.documentElement.classList.contains('wsi-product');
    insetsOnly = Array.isArray(only) && only.length ? only : null;
    syncInsets();
}

// Ridisegna i pallini in tutti i riquadri montati. NON gata su im.loaded(), che
// resta false finché le tile raster ESRI si assestano: con quel gate lo scatto bot
// coglieva l'inset vuoto (l'evento stazioni arrivava mentre le tile caricavano
// ancora, e l'unico render utile veniva saltato). Basta stile+sorgente pronti.
function redrawInsets() {
    for (const [id, im] of insetMaps) {
        const def = INSETS.find(d => d.id === id);
        if (def && im.getSource('ins-st')) renderInsetStations(im, def);
    }
}

export function initMisc(mapInstance) {
    map = mapInstance;
    document.addEventListener('wsv2:stations', () => {
        if (!insetsOn) return;
        // La provincia attiva può essere cambiata (es. clic su AG): riallinea quali
        // riquadri mostrare PRIMA di ridisegnare. No-op se l'insieme non cambia.
        syncInsets();
        redrawInsets();
    });
    // Ingresso/uscita dal futuro (timeline): la mappa principale spegne/riaccende i
    // pallini live; i riquadri-isola devono seguirla (bug 2026-07-05).
    document.addEventListener('wsv2:stations-mode', () => {
        if (insetsOn) redrawInsets();
    });
    // Nuovo render previsioni (anche scrub timeline nel futuro): aggiorna la
    // previsione mostrata nei riquadri-isola.
    document.addEventListener('wsv2:forecast', () => {
        if (insetsOn && isFutureHidden()) redrawInsets();
    });
}
